import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { HttpClientService } from "./http-client.service";
import { Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";

@Injectable()
export class AccountsService {
  private accountingAccountRequests = new Set<number>();
  private accountingAccountRequestChanges = new Subject<{ accountId: number; pending: boolean }>();

  public accountingAccountRequestChanges$ = this.accountingAccountRequestChanges.asObservable();

  constructor(private http: HttpClientService) {}

  public getPersonAccounts(id_person, status?): Observable<any> {
    const options = {
      params: { status },
    };
    return this.http
      .get(`/api/accounts/${id_person}/person_accounts`, options)
      .pipe(tap((_) => console.log("AccountsService.getPersonAccounts: Ok.")));
  }
  public getAccountPayments(account_id): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/account_payments`)
      .pipe(tap((_) => console.log("AccountsService.getAccountPayments: Ok.")));
  }
  public getAccounts(page_number, page_size, status?, search_id?, payment_status?, closed_by?): Observable<any> {
    const options = {
      params: {
        page_number,
        page_size,
        status,
        search_id,
        payment_status,
        closed_by,
      },
    };
    return this.http
      .get("/api/accounts/by_page", options)
      .pipe(tap((_) => console.log("AccountsService.getAccounts: Ok.")));
  }
  public getAccountInformation(id): Observable<any> {
    return this.http
      .get(`/api/accounts/${id}/account_information`)
      .pipe(tap((_) => console.log("AccountsService.getAccountInformation: Ok.")));
  }
  public getMonthlyPaymentPlan(date: string): Observable<any> {
    const options = {
      params: {
        date,
      },
    };
    return this.http
      .get("/api/accounts/monthly_payment_plan", options)
      .pipe(tap((_) => console.log("AccountsService.getMonthlyPaymentPlan: Ok.")));
  }

  public createAccount(account): Observable<any> {
    const options = {
      account,
    };
    return this.http
      .post("/api/accounts/single_create", options)
      .pipe(tap((_) => console.log("AccountsService.createAccount: Ok.")));
  }

  public approveAccount(id): Observable<any> {
    return this.http
      .post(`/api/accounts/${id}/approve`, {})
      .pipe(tap((_) => console.log("AccountsService.approveAccount: Ok.")));
  }
  public rejectAccount(id): Observable<any> {
    return this.http
      .post(`/api/accounts/${id}/reject`, {})
      .pipe(tap((_) => console.log("AccountsService.rejectAccount: Ok.")));
  }
  public getUnverified(): Observable<any> {
    return this.http
      .get("/api/accounts/unverified")
      .pipe(tap((_) => console.log("AccountsService.getUnverified: Ok.")));
  }
  public getUnverifiedCount(): Observable<any> {
    return this.http
      .get("/api/accounts/unverified_count")
      .pipe(tap((_) => console.log("AccountsService.getUnverifiedCount: Ok.")));
  }
  public getAccountsCsv(status?, payment_status?, closed_by?): Observable<any> {
    const options = {
      responseType: "blob" as "json",
      params: {
        status,
        payment_status,
        closed_by,
      },
    };
    return this.http
      .get("/api/accounts/accounts_to_csv", options)
      .pipe(tap((_) => console.log("AccountsService.getAccountsCsv: Ok.")));
  }
  public updateAccount(account): Observable<any> {
    const options = {
      account,
    };
    return this.http
      .put(`/api/accounts/${account.id}`, options)
      .pipe(tap((_) => console.log("AccountsService.updateAccount: Ok.")));
  }

  public validForPaymentDate(payment_date): Observable<any> {
    const options = {
      params: {
        payment_date,
      },
    };
    return this.http
      .get("/api/accounts/valid_for_payment_date", options)
      .pipe(tap((_) => console.log("AccountsService.validForPaymentDate: Ok.")));
  }

  public getApprovedAccounts(): Observable<any> {
    return this.http
      .get("/api/accounts/accounts_approved")
      .pipe(tap((_) => console.log("AccountsService.getApprovedAccounts: Ok")));
  }

  public reports(id_type: string, ids: string): Observable<any> {
    const options = {
      id_type,
      ids,
    };
    return this.http
      .post("/api/accounts/reports", options, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("AccountsService.reports")));
  }

  public getVehicle(account): Observable<any> {
    return this.http
      .get(`/api/accounts/${account.id}/vehicle`)
      .pipe(tap((_) => console.log("AccountsService.getVehicle: Ok")));
  }

  public getBankAccounts(account): Observable<any> {
    return this.http
      .get(`/api/accounts/${account.id}/bank_accounts`)
      .pipe(tap((_) => console.log("AccountsService.getBankAccounts: Ok")));
  }

  public getComercialUsers(account_id): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/comercial_users`)
      .pipe(tap((_) => console.log("AccountsService.getComercialUsers: Ok")));
  }

  public putManagement(account_id, params): Observable<any> {
    const options = {
      account: { ...params },
    };
    return this.http
      .put(`/api/accounts/${account_id}/update_management`, options)
      .pipe(tap((_) => console.log("AccountsService.getComercialUsers: Ok")));
  }

  public getStpAccountData(account_id): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/stp_account_data`)
      .pipe(tap((_) => console.log("AccountsService.getStpAccountData: Ok")));
  }

  public getStpAccountDataAvailability(account_id): Observable<{ available: boolean; retry_at: string | null }> {
    return this.http.get(`/api/accounts/${account_id}/stp_account_data_availability`);
  }

  public sendStpAccountData(account_id, data: { emails: any; phones: any; amount?: number }): Observable<any> {
    const params = {
      ...data,
    };
    return this.http
      .post(`/api/accounts/${account_id}/send_stp_account_data`, params)
      .pipe(tap((_) => console.log("AccountsService.sendStpAccountData: Ok")));
  }

  public sendStpAccountDataForCollection(
    account_id: number,
    data: { phone: string; amount: number; full_name: string }
  ): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/send_stp_account_data_for_collection`, data)
      .pipe(tap((_) => console.log("AccountsService.sendStpAccountDataForCollection: Ok")));
  }

  public setEmailResponse(account_id, data: { vin; response }): Observable<any> {
    const params = {
      ...data,
    };
    return this.http
      .post(`/api/accounts/${account_id}/email_response`, params)
      .pipe(tap((_) => console.log("AccountsService.setEmailResponse: Ok")));
  }
  public getEartlySettlement(account_id): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/early_settlement`)
      .pipe(tap((_) => console.log("AccountsService.getEartlySettlement: Ok")));
  }
  public setEarlySettlement(
    account_id: number,
    email: string,
    change_property: number,
    debts: number,
    tenure: number,
    low_plates: number
  ): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/early_settlement`, { email, change_property, debts, tenure, low_plates })
      .pipe(tap((_) => console.log("AccountsService.setEartlySettlement: Ok")));
  }

  public notifyEarlySettlement(account_id: any): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/early_settlement/notify_early_settlement`, {})
      .pipe(tap((_) => console.log("AccountsService.notifyEarlySettlement: Ok")));
  }

  public notifyPendingsPayments(account_id: any): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/early_settlement/notify_pendings_payments`, {})
      .pipe(tap((_) => console.log("AccountsService.notifyPendingsPayments: Ok")));
  }

  public notifyCsReceiptReceived(account_id: any): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/early_settlement/notify_receipt_received`, {}, {})
      .pipe(tap((_) => console.log("AccountsService.notifyCsReceiptReceived: Ok")));
  }
  public getContributions(account_id): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/contributions`)
      .pipe(tap((_) => console.log("AccountsService.getContributions: Ok")));
  }

  public postSaveModification(account_id, modification): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/save_modification`, modification)
      .pipe(tap((_) => console.log("AccountsService.postSaveModification: Ok")));
  }

  public postSaveContribution(account_id, contribution): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/save_contribution`, contribution)
      .pipe(tap((_) => console.log("AccountsService.postSaveContribution: Ok")));
  }

  public getApprovedPaymentsCSV(account_id: number): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/approved_payments_to_csv`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("AccountsService.getApprovedPaymentsCSV: Ok")));
  }

  public updateDisbursementBankAccount(account_id: number, bank_account_id): Observable<any> {
    const options = { bank_account_id };
    return this.http
      .put(`/api/accounts/${account_id}/disbursement_bank_account`, options)
      .pipe(tap((_) => console.log("AccountsService.updateDisbursementBankAccount: Ok.")));
  }

  public getProceduresHistory(account_id: number): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/procedures_history`)
      .pipe(tap((_) => console.log("AccountsService.getProceduresHistory: Ok.")));
  }

  public getProceduresResources(account_id: number): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/procedures_resources`)
      .pipe(tap((_) => console.log("AccountsService.getProceduresResources: Ok.")));
  }

  public getPaymentsHistory(account_id: number, filters: any = {}): Observable<any> {
    let params = new HttpParams();

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== "") {
        // Handle arrays by appending each value separately
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => {
            params = params.append(key, item.toString());
          });
        } else if (!Array.isArray(value)) {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http.get(`/api/accounts/${account_id}/payments_history`, { params }).pipe(
      map((response: any) => ({
        ...response,
        payments: response.payments || [],
      })),
      tap((_) => console.log("AccountsService.getPaymentsHistory: Ok."))
    );
  }

  public createMoratory(paymentId: number, params: { amount: number }) {
    return this.http
      .post(`/api/payments/${paymentId}/moratory_registration`, params)
      .pipe(tap((_) => console.log("AccountsService.createMoratory: Ok (stub).")));
  }

  public updateMoratory(paymentId: number, params: { amount: number }): Observable<{ status: string }> {
    return this.http
      .put(`/api/payments/${paymentId}/moratory_registration`, params)
      .pipe(tap((_) => console.log("AccountsService.updateMoratory: Ok (stub).")));
  }

  public deleteMoratory(paymentId: number) {
    return this.http
      .delete(`/api/payments/${paymentId}/moratory_registration`)
      .pipe(tap((_) => console.log("AccountsService.deleteMoratory: Ok (stub).")));
  }

  public getPaymentsHistoryDetails(account_id: number): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/payments_history_details`)
      .pipe(tap((_) => console.log("AccountsService.getPaymentsHistoryDetails: Ok.")));
  }

  public forgiveMoratoryInterest(
    account_id: number,
    params: { amount?: number; justification: string }
  ): Observable<any> {
    return this.http
      .post(`/api/accounts/${account_id}/payment_plan/forgive_moratory_interest`, params)
      .pipe(tap((_) => console.log("AccountsService.forgiveMoratoryInterest: Ok.")));
  }

  public getApplicationAddresses(account_id: number): Observable<any> {
    return this.http
      .get(`/api/accounts/${account_id}/application_addresses`)
      .pipe(tap((_) => console.log("AccountsService.getApplicationAddresses: Ok.")));
  }

  public createAccountingAccount(accountId: number): Observable<any> {
    this.accountingAccountRequests.add(accountId);
    this.accountingAccountRequestChanges.next({
      accountId,
      pending: true,
    });

    return this.http.post(`/api/accounts/${accountId}/accounting_account_request`, {}).pipe(
      tap({
        next: () => {
          console.log("AccountsService.createAccountingAccount: Ok.");
        },
        error: () => {
          this.accountingAccountRequests.delete(accountId);
          this.accountingAccountRequestChanges.next({
            accountId,
            pending: false,
          });
        },
      })
    );
  }

  public hasRecentAccountingAccountRequest(accountId: number): boolean {
    return this.accountingAccountRequests.has(accountId);
  }
}
